'use client';

import type { CategoryDesktop, CategoryMedium } from '@models/category';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import type { IBreadcrumbItem } from '@wconcept/wconcept-ui-renewal/components/molecules/_desktop/BreadcrumbDesktop';
import { useMemo } from 'react';

/**
 * PC 브레드크럼 데이터 (navigationPc 기반). 홈 › 몰 › 대▾ › 중▾ › 소▾
 * 홈/몰은 서버 landingUrl 링크, 대/중/소는 같은 레벨 형제 드롭다운(코드 매칭). 코드 인자는 '000' 표기.
 */
export function useCategoryBreadcrumb({
  navigationPc,
  mediumCd,
  largeCategory,
  middleCategory,
  smallCategory,
}: {
  navigationPc: CategoryDesktop | undefined;
  mediumCd: string;
  largeCategory: string;
  middleCategory: string;
  smallCategory: string;
}): IBreadcrumbItem[] | undefined {
  return useMemo(() => {
    const categoryPath = navigationPc?.categoryPath;
    if (!mediumCd || !categoryPath?.length) {
      return undefined;
    }

    // href 있는 Button(<a>) — BreadcrumbDesktop 이 title 을 <button> 으로 감싸므로 button 중첩 방지
    const link = (label: string, url: string | undefined, active = false) => (
      <Button href={url} className={active ? 'active' : undefined}>
        {label}
      </Button>
    );

    // 대분류 목록: 몰 루트 노드가 있으면 그 자식, 없으면 같은 mediumCd 최상위 노드들
    const mallNodes =
      navigationPc?.largeCategoryList?.filter(
        node => node.mediumCd === mediumCd
      ) ?? [];
    const mallRoot = mallNodes.find(node => node.largeCategory === '000');
    const largeList = mallRoot ? mallRoot.subCategory : mallNodes;

    const largeNode = largeList?.find(
      node => node.largeCategory === largeCategory
    );
    const middleList = largeNode?.subCategory?.filter(
      node => node.largeCategory === largeCategory
    );
    const middleNode = middleList?.find(
      node => node.middleCategory === middleCategory
    );
    const smallList = middleNode?.subCategory?.filter(
      node =>
        node.largeCategory === largeCategory &&
        node.middleCategory === middleCategory
    );

    // index 별 [형제 목록, 현재 판정]
    const levels: [
      CategoryMedium[] | undefined,
      (node: CategoryMedium) => boolean
    ][] = [
      [largeList, node => node.largeCategory === largeCategory],
      [middleList, node => node.middleCategory === middleCategory],
      [smallList, node => node.smallCategory === smallCategory],
    ];

    const toMenu = (
      siblings: CategoryMedium[] | undefined,
      isActive: (node: CategoryMedium) => boolean
    ) =>
      siblings?.length
        ? {
            items: siblings.map(node => ({
              label: link(node.categoryName, node.landingUrl, isActive(node)),
            })),
          }
        : undefined;

    return categoryPath.map((path, index) => {
      if (index <= 1) {
        return { title: link(path.categoryTitle, path.landingUrl) };
      }
      const level = levels[index - 2];
      return {
        title: path.categoryTitle,
        menu: level ? toMenu(level[0], level[1]) : undefined,
      };
    });
  }, [navigationPc, mediumCd, largeCategory, middleCategory, smallCategory]);
}
